import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const SIGNAL_TYPES = [
  'magic_link_unopened_24h',
  'docs_under_50pct_at_t7',
  'portal_inactive_t-5',
  'missed_signature',
  'sla_breach',
];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const usePbNoShowSignalsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/preboarding-noshow-signals?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const resolve = useCallback(async (id, note) => {
    try { await api.post(`/v1/preboarding-noshow-signals/${id}/resolve`, { resolutionNote: note }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const scan = useCallback(async () => {
    try { await api.post(`/v1/preboarding-noshow-signals/scan`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const signalLabel = (s) => t(`preboardingSignal.type.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'preboardingId', label: 'PB', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName', label: t('preboardingSignal.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'signalType', label: t('preboardingSignal.signalType'),
        options: {
          ...multiSelectFilter(t('preboardingSignal.signalType'), SIGNAL_TYPES, signalLabel),
          customBodyRender: (v) => signalLabel(v),
        },
      },
      {
        name: 'riskLevelDelta', label: t('preboardingSignal.riskDelta'),
        options: { filter: false, sort: true, customBodyRender: (v) => `-${v}` },
      },
      {
        name: 'description', label: t('preboardingSignal.description'),
        options: { filter: false, sort: false },
      },
      dateFilterColumn('detectedAt', t('preboardingSignal.detectedAt'), lang),
      {
        name: 'resolvedAt', label: t('preboardingSignal.resolvedAt'),
        options: {
          filter: false, sort: true,
          customBodyRender: (v) => v ? new Date(v).toLocaleString() : '–',
        },
      },
    ];
  }, [t, lang, data]);

  return { fetching, data, pagination, columns, refresh, resolve, scan };
};

export default usePbNoShowSignalsApi;
